import React from 'react';
import { Heart, Target, Eye, Users } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { aboutContent, teamMembers, facilities } from '../data/mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1551076805-e1869033e561"
            alt="Klinik"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Hakkımızda
          </h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            1990'dan beri Kocaeli'nde evcil dostlarınıza sevgi ve özenle hizmet ediyoruz
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutContent.stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-br from-emerald-50 to-amber-50 rounded-xl">
                <div className="text-5xl font-bold text-emerald-800 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-emerald-900 mb-6">
                Hikayemiz
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {aboutContent.history}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Veterinerlerimiz cerrahi, iç hastalıklar, dermatoloji ve aşılama konularında uzmanlaşmıştır. 
                Her hayvanın benzersiz ihtiyaçlarını karşılamak için özel tedavi planları geliştirir. 
                Laboratuvar testleri, ultrason ve röntgen gibi ileri tanı araçlarını kullanarak kesin teşhis ve zamanında tedavi sağlarlar.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1654895716780-b4664497420d"
                alt="Veteriner bakım"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold text-emerald-800">%95+</div>
                <div className="text-gray-600">Memnuniyet</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 rounded-2xl">
              <div className="bg-emerald-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-900 mb-4">Misyonumuz</h3>
              <p className="text-gray-700 leading-relaxed">
                {aboutContent.mission} En son tıbbi teknolojileri kullanır, sürekli eğitim alır 
                ve hayvan sağlığında en iyi uygulamaları benimseriz.
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-8 rounded-2xl">
              <div className="bg-amber-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <Eye className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-amber-900 mb-4">Vizyonumuz</h3>
              <p className="text-gray-700 leading-relaxed">
                {aboutContent.vision} Sabırlı, şefkatli ve profesyonel bir yaklaşım benimser, 
                her hastaya en yüksek kalitede bakım sağlarız.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Uzman Ekibimiz
            </h2>
            <p className="text-xl text-gray-600">
              Alanında uzman ve deneyimli veterinerlerimizle tanışın
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all group"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                    <p className="text-emerald-200 font-medium mb-1">{member.title}</p>
                    <p className="text-sm text-emerald-100">{member.experience}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Olanaklar ve İmkanlar
            </h2>
            <p className="text-xl text-gray-600">
              Konforunuz için sunduğumuz tüm imkanlar
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {facilities.map((facility, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-emerald-50 to-amber-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow"
              >
                <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Heart className="h-6 w-6 text-emerald-700" />
                </div>
                <p className="font-medium text-gray-800">{facility}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-800 via-emerald-700 to-amber-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <Users className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">
              %95+ Pet Sahibi Bizi Tavsiye Ediyor
            </h2>
            <p className="text-xl text-emerald-50 max-w-3xl mx-auto mb-8">
              En son tıbbi teknolojileri kullanıyor, sürekli eğitim alıyor ve hayvan sağlığında 
              en iyi uygulamaları benimsiyoruz. Sabırlı, şefkatli ve profesyonel bir yaklaşım 
              ile her hastaya en yüksek kalitede bakım sağlıyoruz.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
